import { Link } from 'react-router';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Icon,
  Reason,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from '@efira/ui';

import { useState } from 'react';

import {
  useBilling,
  useCancelSubscription,
  useChangePeriod,
  useMe,
  usePeriodChange,
  useQuota,
  useResumeSubscription,
} from '../../api/queries';
import type { BillingPeriod } from '../../api/queries';
import { ProStatus, usePlanState } from '../ProStatus';

/**
 * Оплата: тариф, подписка, следующее списание, способ оплаты, история, чеки.
 *
 * ВСЁ ЧИТАЕТСЯ ИЗ НАШЕЙ БАЗЫ, а не из платёжного сервиса: страница про
 * деньги обязана открываться, даже когда сервис недоступен. К сервису
 * обращаемся только тогда, когда человек что-то МЕНЯЕТ.
 *
 * ЧЕГО ЗДЕСЬ НЕТ: выдуманной карты «•••• 4242» и выдуманной даты списания.
 * Способ оплаты неизвестен, пока провайдер не подключён, - так и написано.
 * Нарисованная карта в разделе про деньги - это заявление о том, что мы
 * храним чужие платёжные данные, которых у нас нет.
 *
 * ПУСТАЯ ИСТОРИЯ ОБЪЯСНЕНА, а не оставлена пустотой: человек, открывший
 * «Оплату» впервые, должен понять, что он ничего не пропустил.
 */

/** Состояние подписки словами стримера, а не строками базы. */
const STATUS_WORDS: Record<string, string> = {
  active: 'Активна',
  cancelling: 'Отменена, доработает до конца периода',
  past_due: 'Платёж не прошёл',
  ended: 'Закончилась',
};

function day(iso: string | null): string {
  return iso === null ? '—' : new Date(iso).toLocaleDateString('ru-RU');
}

export function Billing() {
  const me = useMe();
  const quota = useQuota();
  const billing = useBilling();
  const cancel = useCancelSubscription();
  const resume = useResumeSubscription();
  const changePeriod = useChangePeriod();
  const toast = useToast();
  /* Смена периода показывается ДО подтверждения: это деньги, и человек
     обязан увидеть, что станет с оплаченным остатком, заранее. */
  const [switching, setSwitching] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const plan = me.data?.user?.plan;
  const planState = usePlanState();
  const daily = quota.data?.dailyMinutes ?? plan?.dailyMinutes ?? null;
  const data = billing.data;
  const subscription = data?.subscription ?? null;

  return (
    <div data-billing="root" className="flex max-w-xl flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h2 className="text-foreground text-xl font-semibold">Оплата</h2>
        <p className="text-foreground/80 text-sm">Тариф, подписка, способ оплаты и чеки — здесь.</p>
      </header>

      {/*
        КАРТОЧКИ РАЗДЕЛА - ПО БЛОКУ `card-with-form` (единица 19).

        Было: `section` с рамкой, радиусом и отступом, набранными классами
        по месту, - то есть карточка, собранная руками рядом с карточкой
        пресета. Стало: `Card` с его же шапкой, телом и подвалом. Своей
        разметки рамы не остаётся ни строки.
      */}
      <Card
        data-billing="plan"
        data-control-row="billing-plan"
        size="sm"
        className="@container flex-row items-center gap-3"
      >
        <span className="min-w-0 flex-1">
          {/* Ключ и значение разведены КЕГЛЕМ, а не двоеточием: название
              тарифа - главное значение экрана, и оно должно читаться
              первым взглядом. */}
          <span className="text-foreground/80 block text-xs tracking-wide uppercase">Тариф</span>
          <span className="text-foreground block text-xl font-semibold">{planState.name}</span>
          <span className="text-foreground/90 block text-sm">
            {daily === null
              ? 'Эфир без ограничения по времени.'
              : `Эфир — ${String(daily)} минут в сутки. Симулятор не расходует это время.`}
          </span>
        </span>

        <span className="flex shrink-0 flex-col items-end gap-1">
          <Link data-billing="upgrade" to="/app/plan?from=billing">
            {/* Не «Перейти на Pro»: перейти пока нельзя, и кнопка, обещающая
                переход, обманывает раньше, чем человек нажмёт. */}
            Что даёт Pro
          </Link>
          {planState.current === 'free' && <ProStatus />}
        </span>
      </Card>

      {/* ПОДПИСКА - отдельной карточкой, и только когда она есть. Пустая
          карточка «подписки нет» повторяла бы строку тарифа выше. */}
      {subscription !== null && (
        <Card data-billing="subscription" size="sm" className="gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-foreground/80 text-xs tracking-wide uppercase">Подписка</span>
            <span data-billing="status" className="text-foreground text-sm font-medium">
              {subscription.status === 'active'
                ? `Активна, оплата за ${subscription.period === 'year' ? 'год' : 'месяц'}`
                : (STATUS_WORDS[subscription.status] ?? subscription.status)}
            </span>
          </div>

          {/* Следующее списание: дата И сумма. Дата без суммы отвечает на
              половину вопроса, ради которого сюда пришли. */}
          {/* Сумма НАША, из прайса: налоги страны плательщика и зачёт
              остатка считает платёжный сервис. «Спишем ровно столько» было
              бы той же ошибкой, что цена, набранная в разметке. */}
          <p data-billing="next-charge" className="text-foreground/90 text-sm">
            {data?.nextCharge === null || data?.nextCharge === undefined
              ? subscription.status === 'cancelling'
                ? `Списаний больше не будет. Доступ до ${day(subscription.currentPeriodEnd)}.`
                : data?.retrying === true
                  ? 'Платёж не прошёл — платёжный сервис повторит попытку. Если списание не нужно, отмените подписку.'
                  : 'Следующего списания нет.'
              : `Следующее списание ${day(data.nextCharge.at)} — около ${data.nextCharge.text}; ` +
                'точную сумму с налогами назовёт платёжный сервис.'}
          </p>

          {subscription.graceEndsAt !== null && (
            <p
              data-billing="grace"
              className="border-warning/50 bg-warning/10 rounded-lg border px-3 py-2 text-sm"
            >
              Платёж не прошёл. Pro работает до {day(subscription.graceEndsAt)} — если оплата не
              пройдёт, тариф станет бесплатным.
            </p>
          )}

          <p data-billing="method" className="text-foreground/80 text-sm">
            Способ оплаты — {data?.method.text ?? 'неизвестен'}
          </p>

          {/*
            СМЕНА ПЕРИОДА - ТОЛЬКО У АКТИВНОЙ ПОДПИСКИ.
            Находка владельца: у отменённой рядом стояли «Возобновить» и
            «Перейти на оплату за месяц» - действие раньше своего порядка.
            У отменённой сначала возврат, у просроченной сначала платёж, у
            закончившейся - оформление заново. Менять период имеет смысл
            только там, где он ещё будет применён.
          */}
          {subscription.status === 'active' && (
            <PeriodSwitch
              from={subscription.period}
              open={switching}
              refused={refused}
              pending={changePeriod.isPending}
              onOpen={() => {
                setRefused(null);
                setSwitching((value) => !value);
              }}
              onConfirm={(next) => {
                changePeriod.mutate(next, {
                  onSuccess: (result) => {
                    if (result.ok) {
                      setSwitching(false);
                      toast.show({ tone: 'success', title: 'Период оплаты изменён' });

                      return;
                    }

                    setRefused(result.message ?? 'Сменить период пока нельзя.');
                  },
                });
              }}
            />
          )}

          {subscription.status === 'ended' && (
            /* У закончившейся подписки нет ни отмены, ни возврата: её
               оформляют заново, и кнопка для этого - наверху карточки
               тарифа, а не здесь. Второй такой же кнопки не будет. */
            <p data-billing="ended-hint" className="text-foreground/90 text-sm">
              Подписка закончилась. Оформить Pro заново можно кнопкой «Что даёт Pro» выше.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {subscription.status === 'cancelling' ? (
              <Button
                variant="primary"
                size="s"
                data-billing="resume"
                loading={resume.isPending}
                onClick={() => {
                  resume.mutate(undefined, {
                    onSuccess: (result) => {
                      /* Что именно сделано - по ответу сервера, а не по
                         факту нажатия: у платёжного сервиса возврат может
                         не пройти, и обещать продление за него нельзя. */
                      toast.show({
                        tone: result.provider === 'ok' ? 'success' : 'warning',
                        title: 'Подписка возобновлена у нас',
                        text:
                          result.provider === 'ok'
                            ? 'Списания продолжатся в обычные сроки.'
                            : 'Платёжный сервис возврат пока не подтвердил — напишите нам, если продление не пройдёт.',
                      });
                    },
                  });
                }}
              >
                Возобновить подписку
              </Button>
            ) : subscription.status === 'ended' ? null : (
              <Button
                variant="secondary"
                size="s"
                data-billing="cancel"
                disabled={cancel.isPending}
                onClick={() => {
                  cancel.mutate(undefined, {
                    onSuccess: (result) => {
                      /*
                       * САМАЯ ДОРОГАЯ СТРОКА КОНТУРА (разбор текстов).
                       *
                       * Первая редакция говорила «подписка отменена» всегда.
                       * Наша запись действительно меняется всегда - а у
                       * платёжного сервиса отмена может не пройти, и тогда
                       * списание повторится у человека, которому сказали,
                       * что он отменил. Говорим ровно то, что случилось.
                       */
                      toast.show({
                        tone: result.provider === 'ok' ? 'success' : 'warning',
                        title:
                          result.provider === 'ok'
                            ? 'Подписка отменена'
                            : 'Отменили у себя, сервис не подтвердил',
                        text:
                          result.provider === 'ok'
                            ? 'Доступ сохраняется до конца оплаченного периода. Вернуть можно здесь же.'
                            : 'Доступ сохраняется до конца оплаченного периода. Платёжный сервис отмену не подтвердил — напишите нам, если пройдёт списание.',
                      });
                    },
                  });
                }}
              >
                Отменить подписку
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* ИСТОРИЯ И ЧЕКИ - ТАКОЙ ЖЕ КАРТОЧКОЙ, как тариф и подписка: блок без
          контейнера рядом с двумя обрамлёнными читается как незавершённая
          вёрстка (находка ревью раскладки). */}
      <Card data-billing="history" size="sm" className="gap-2">
        <CardHeader className="p-0">
          <CardTitle>Последние платежи</CardTitle>
        </CardHeader>

        {(data?.payments.length ?? 0) === 0 ? (
          <p
            data-billing="history-empty"
            className="border-foreground/10 rounded-lg text-foreground/80 border border-dashed p-3 text-sm"
          >
            {data?.emptyHint ?? 'Списаний пока не было.'}
          </p>
        ) : (
          /* ИСТОРИЯ ПЛАТЕЖЕЙ - ТАБЛИЦА, роль из таблицы ролей. Было
             перечисление с сеткой из четырёх колонок, набранной классом:
             колонки без заголовков, и человек догадывался, что есть что. */
          <Table data-billing="history-table">
            <TableHeader>
              <TableRow>
                <TableHead>Дата</TableHead>
                <TableHead>Сумма</TableHead>
                <TableHead>Период</TableHead>
                <TableHead>Чек</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.payments ?? []).map((payment) => (
                <TableRow
                  key={payment.id}
                  data-billing-payment={payment.id}
                  data-control-row="billing-payment"
                >
                  <TableCell className="tabular-nums">{day(payment.paidAt)}</TableCell>
                  <TableCell className="tabular-nums">{payment.text}</TableCell>
                  <TableCell>{payment.period === 'year' ? 'за год' : 'за месяц'}</TableCell>
                  <TableCell>
                    {payment.receiptUrl === null ? (
                      /* Чека нет - так и сказано. Кнопка, ведущая в никуда,
                         хуже её отсутствия. */
                      <span>чек не пришёл</span>
                    ) : (
                      <Button asChild variant="ghost" size="s" className="gap-1">
                        <a
                          data-billing-receipt={payment.id}
                          href={payment.receiptUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Icon name="open" size="sm" />
                          Чек
                        </a>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Состояние оплаты в продукте - одной строкой и честно. Строка
          «списаний нет и быть не может» НЕ показывается тому, у кого
          списания в истории есть: два утверждения на одном экране, и одно
          из них человек видит своими глазами опровергнутым. */}
      {data?.provider.connected !== true && (data?.payments.length ?? 0) === 0 && (
        <p data-billing="not-connected" className="text-foreground/90 text-sm">
          Платёжный сервис ещё подключается: списаний нет и быть не может. Цена Pro уже назначена и
          видна в окне тарифа.
        </p>
      )}
    </div>
  );
}

/**
 * Переключение месяц ↔ год с пояснением ДО подтверждения.
 *
 * Пояснение приходит с сервера (`/api/billing/period-change`): что сейчас,
 * что станет и что будет с оплаченным остатком. Сумму к списанию мы не
 * называем - её считает платёжный сервис, и наша цифра разошлась бы с ней.
 */
function PeriodSwitch({
  from,
  open,
  refused,
  pending,
  onOpen,
  onConfirm,
}: {
  from: BillingPeriod;
  open: boolean;
  refused: string | null;
  pending: boolean;
  onOpen: () => void;
  onConfirm: (period: BillingPeriod) => void;
}) {
  const target: BillingPeriod = from === 'year' ? 'month' : 'year';
  const preview = usePeriodChange(target, open);

  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="ghost"
        size="s"
        className="self-start px-0"
        data-billing="period-switch"
        aria-expanded={open}
        onClick={onOpen}
      >
        {target === 'year' ? 'Перейти на оплату за год' : 'Перейти на оплату за месяц'}
      </Button>

      {open && (
        <div
          data-billing="period-preview"
          className="border-foreground/15 rounded-lg flex flex-col gap-2 border border-dashed p-3 text-sm"
        >
          {(preview.data?.lines ?? ['Считаем…']).map((line) => (
            <p key={line} className="text-foreground/90">
              {line}
            </p>
          ))}

          {preview.data?.periodEnd !== null && preview.data?.periodEnd !== undefined && (
            <p className="text-foreground/90">
              Текущий период оплачен до{' '}
              {new Date(preview.data.periodEnd).toLocaleDateString('ru-RU')}.
            </p>
          )}

          {refused !== null && (
            <p data-billing="period-refused" className="text-warning">
              {refused}
            </p>
          )}

          <Button
            variant="primary"
            size="s"
            className="self-start"
            data-billing="period-confirm"
            loading={pending}
            disabled={preview.data?.possible !== true}
            aria-describedby={preview.data?.possible === true ? undefined : 'billing-period-why'}
            onClick={() => {
              onConfirm(target);
            }}
          >
            {target === 'year' ? 'Перейти на год' : 'Перейти на месяц'}
          </Button>
          {preview.data?.possible !== true && (
            <Reason id="billing-period-why">
              {preview.data === undefined
                ? 'Считаем, во что обойдётся переход.'
                : 'Сейчас перейти нельзя — причина в строках выше.'}
            </Reason>
          )}
        </div>
      )}
    </div>
  );
}
